import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CHAT_CONFIG } from '@the-wall/shared';
import { useChatStore } from '@/stores/chatStore';
import { useAuthStore } from '@/stores/authStore';
import { useCanvasStore } from '@/stores/canvasStore';
import { sendChatMessage, deleteChatMessage } from '@/services/socket';
import { UserBadges } from '@/components/UserBadges';
import { UsernameText } from '@/components/UsernameText';
import type { ChatMessage } from '@the-wall/shared';

const MODAL_WIDTH = 320;
const MODAL_HEIGHT = 380;
const FAB_SIZE = 56;
const PADDING = 16;

function getInitialPosition() {
  if (typeof window === 'undefined') return { x: 0, y: 0 };
  const x = window.innerWidth - MODAL_WIDTH - PADDING;
  const y = window.innerHeight - MODAL_HEIGHT - PADDING - FAB_SIZE;
  return { x: Math.max(PADDING, x), y: Math.max(PADDING, y) };
}

function getViewportBounds() {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const effectiveWidth = Math.min(MODAL_WIDTH, w - PADDING * 2);
  const effectiveHeight = Math.min(MODAL_HEIGHT, h - PADDING * 2);
  return {
    maxX: w - effectiveWidth - PADDING,
    maxY: h - effectiveHeight - PADDING,
  };
}

function clampPosition(x: number, y: number) {
  const { maxX, maxY } = getViewportBounds();
  return {
    x: Math.max(PADDING, Math.min(maxX, x)),
    y: Math.max(PADDING, Math.min(maxY, y)),
  };
}

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChatModal({ isOpen, onClose }: ChatModalProps) {
  const [position, setPosition] = useState(getInitialPosition);
  const dragStart = useRef({ x: 0, y: 0, left: 0, top: 0 });

  const handleHeaderPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if ((e.target as HTMLElement).closest('button')) return;
      e.preventDefault();
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);

      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        left: position.x,
        top: position.y,
      };

      const onMove = (e: PointerEvent) => {
        e.preventDefault();
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        const next = clampPosition(dragStart.current.left + dx, dragStart.current.top + dy);
        dragStart.current = { x: e.clientX, y: e.clientY, left: next.x, top: next.y };
        setPosition(next);
      };

      const stopDrag = () => {
        target.releasePointerCapture(e.pointerId);
        target.removeEventListener('pointermove', onMove);
        target.removeEventListener('pointerup', stopDrag);
        target.removeEventListener('pointercancel', stopDrag);
      };

      // passive: false so preventDefault() in onMove works and stops browser scroll on mobile
      target.addEventListener('pointermove', onMove, { passive: false });
      target.addEventListener('pointerup', stopDrag, { once: true });
      target.addEventListener('pointercancel', stopDrag, { once: true });
    },
    [position.x, position.y]
  );

  // Reset position when opening
  useEffect(() => {
    if (isOpen) {
      setPosition(getInitialPosition());
    }
  }, [isOpen]);

  // Keep modal in viewport when window is resized
  useEffect(() => {
    if (!isOpen) return;
    const onResize = () => {
      setPosition((prev) => clampPosition(prev.x, prev.y));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-40 pointer-events-none">
      <div
        className="absolute flex flex-col bg-wall-surface border border-wall-border rounded-xl shadow-2xl overflow-hidden pointer-events-auto"
        style={{
          width: MODAL_WIDTH,
          height: MODAL_HEIGHT,
          maxWidth: `min(${MODAL_WIDTH}px, calc(100vw - ${PADDING * 2}px))`,
          maxHeight: `min(${MODAL_HEIGHT}px, calc(100vh - ${PADDING * 2}px))`,
          left: position.x,
          top: position.y,
        }}
      >
        {/* Draggable header */}
        <div
          className="shrink-0 flex items-center justify-between px-3 py-2 border-b border-wall-border/50 bg-wall-bg/80 cursor-grab active:cursor-grabbing touch-none select-none"
          onPointerDown={handleHeaderPointerDown}
        >
          <span className="text-sm font-medium text-wall-text">Chat</span>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-wall-muted hover:text-wall-text hover:bg-wall-border/50 transition-colors touch-manipulation"
            aria-label="Close chat"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Message list + input: flex-1 min-h-0 so this column gets remaining space and doesn't overflow */}
        <div className="flex flex-col flex-1 min-h-0">
          <ChatMessageList />
          <div className="shrink-0">
            <ChatInput onSend={sendChatMessage} />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function formatDayHeader(date: Date): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  const opts: Intl.DateTimeFormatOptions =
    date.getFullYear() !== today.getFullYear()
      ? { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }
      : { weekday: 'long', month: 'short', day: 'numeric' };
  return date.toLocaleDateString(undefined, opts);
}

function getDateKey(createdAt: string | number | Date): string {
  const d = new Date(createdAt);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

// Parse message content and render coordinates as clickable links
function MessageContent({ content }: { content: string }) {
  const navigateToCoordinate = useCanvasStore((state) => state.navigateToCoordinate);
  const canvasWidth = useCanvasStore((state) => state.width);
  const canvasHeight = useCanvasStore((state) => state.height);

  // Pattern to match coordinates like "(185, 130)" or "(0, 0)" or "(255, 255)"
  const coordinatePattern = /\((\d+),\s*(\d+)\)/g;

  const parts: (string | { x: number; y: number; text: string })[] = [];
  let lastIndex = 0;
  let match;

  while ((match = coordinatePattern.exec(content)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }

    const x = parseInt(match[1] ?? '0', 10);
    const y = parseInt(match[2] ?? '0', 10);

    // Only make it clickable if coordinates are valid
    if (x >= 0 && x < canvasWidth && y >= 0 && y < canvasHeight) {
      parts.push({ x, y, text: match[0] });
    } else {
      // Invalid coordinates, just add as text
      parts.push(match[0]);
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  // If no coordinates found, return plain text
  if (parts.length === 1 && typeof parts[0] === 'string') {
    return <>{content}</>;
  }

  return (
    <>
      {parts.map((part, index) => {
        if (typeof part === 'string') {
          return <span key={index}>{part}</span>;
        }
        return (
          <button
            key={index}
            onClick={(e) => {
              e.preventDefault();
              navigateToCoordinate(part.x, part.y);
            }}
            className="text-wall-accent hover:text-wall-accent-hover underline decoration-wall-accent/50 hover:decoration-wall-accent transition-colors font-medium"
            title={`Navigate to (${part.x}, ${part.y})`}
          >
            {part.text}
          </button>
        );
      })}
    </>
  );
}

function ChatMessageList() {
  const messages = useChatStore((state) => state.messages);
  const setReplyingTo = useChatStore((state) => state.setReplyingTo);
  const adminRole = useAuthStore((state) => state.user?.adminRole);
  const canModerate = adminRole === 'SUPER_ADMIN' || adminRole === 'ADMIN';
  const scrollRef = useRef<HTMLDivElement>(null);

  const messagesByDay = useMemo(() => {
    const groups: { dateKey: string; date: Date; messages: typeof messages }[] = [];
    for (const msg of messages) {
      const date = new Date(msg.createdAt);
      const dateKey = getDateKey(msg.createdAt);
      const last = groups[groups.length - 1];
      if (last?.dateKey === dateKey) {
        last.messages.push(msg);
      } else {
        groups.push({ dateKey, date, messages: [msg] });
      }
    }
    return groups;
  }, [messages]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  return (
    <div ref={scrollRef} className="flex-1 overflow-auto pt-0 p-2 min-h-0">
      <div className="flex flex-col gap-1.5">
        {messages.length === 0 ? (
          <p className="text-wall-muted text-sm py-2">No messages yet. Say hi!</p>
        ) : (
          messagesByDay.map(({ dateKey, date, messages: dayMessages }) => (
            <div key={dateKey} className="flex flex-col gap-1.5">
              <div className="sticky top-0 z-10 flex items-center gap-3 pt-3 pb-2 -mx-2 px-2 bg-wall-surface/95 backdrop-blur-sm">
                <span className="text-xs font-semibold text-wall-accent uppercase tracking-wider">
                  {formatDayHeader(date)}
                </span>
                <div className="flex-1 h-px bg-wall-accent/40" aria-hidden />
              </div>
              {dayMessages.map((msg) => {
                const msgDate = new Date(msg.createdAt);
                return (
                  <ChatMessageItem
                    key={msg.id}
                    msg={msg}
                    timeLabel={msgDate.toLocaleTimeString(undefined, {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                    onReply={setReplyingTo}
                    canModerate={canModerate}
                  />
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ChatReplyPreview({
  message,
  className = '',
}: {
  message: NonNullable<ChatMessage['replyTo']>;
  className?: string;
}) {
  return (
    <div
      className={`min-w-0 rounded-md border border-wall-border/45 bg-wall-surface/60 px-2.5 py-1.5 ${className}`}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="h-3.5 w-0.5 rounded-full bg-wall-accent/75 shrink-0" aria-hidden />
        <UsernameText
          text={message.username}
          color={message.usernameColor ?? null}
          effect={message.usernameEffect ?? null}
          className="text-xs font-medium truncate"
        />
      </div>
      <p className="mt-1 truncate text-[11px] leading-4 text-wall-muted">{message.content}</p>
    </div>
  );
}

function ChatMessageItem({
  msg,
  timeLabel,
  onReply,
  canModerate,
}: {
  msg: ChatMessage;
  timeLabel: string;
  onReply: (message: ChatMessage) => void;
  canModerate: boolean;
}) {
  const handleDelete = () => {
    if (window.confirm(`Delete this message from ${msg.username}?`)) {
      deleteChatMessage(msg.id);
    }
  };

  return (
    <div className="group text-sm wrap-break-word rounded-lg px-2.5 py-1.5 bg-wall-bg/60 border border-wall-border/30">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <UsernameText
              text={msg.username}
              color={msg.usernameColor ?? null}
              effect={msg.usernameEffect ?? null}
              className="font-medium text-wall-accent truncate"
            />
            <UserBadges userId={msg.userId} className="align-middle shrink-0" />
            <span className="text-wall-muted text-xs shrink-0">{timeLabel}</span>
          </div>
          {msg.replyTo && <ChatReplyPreview message={msg.replyTo} className="mt-1.5 mb-1.5" />}
          <p className="text-wall-text mt-0.5">
            <MessageContent content={msg.content} />
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={() => onReply(msg)}
            className="shrink-0 rounded-md p-1.5 text-wall-muted hover:text-wall-text hover:bg-wall-border/45 transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
            aria-label={`Reply to ${msg.username}`}
            title={`Reply to ${msg.username}`}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M10 8 6 12l4 4M6 12h7a5 5 0 0 1 5 5"
              />
            </svg>
          </button>
          {canModerate && (
            <button
              type="button"
              onClick={handleDelete}
              className="shrink-0 rounded-md p-1.5 text-wall-muted hover:text-red-400 hover:bg-red-500/15 transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
              aria-label={`Delete message from ${msg.username}`}
              title="Delete message (admin)"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m-7 0v12a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V7"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatInput({ onSend }: { onSend: (content: string) => void }) {
  const [draft, setDraft] = useState('');
  const user = useAuthStore((state) => state.user);
  const replyingTo = useChatStore((state) => state.replyingTo);
  const clearReplyingTo = useChatStore((state) => state.clearReplyingTo);

  const timeoutUntil = user?.timeoutUntil ? new Date(user.timeoutUntil) : null;
  const isTimedOut = timeoutUntil !== null && timeoutUntil > new Date();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed || !user) return;
    if (isTimedOut || trimmed.length > CHAT_CONFIG.MESSAGE_MAX_LENGTH) return;
    onSend(trimmed);
    setDraft('');
  };

  const disabled = !user || isTimedOut;
  const placeholder = !user
    ? 'Log in to chat'
    : isTimedOut
      ? 'You are timed out'
      : 'Type a message...';

  return (
    <form onSubmit={handleSubmit} className="p-2 border-t border-wall-border/50 bg-wall-bg/40">
      {replyingTo && (
        <div className="mb-2 flex items-start gap-2 rounded-lg border border-wall-border/45 bg-wall-surface/70 px-2.5 py-2">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-wall-accent">
              Replying to
            </p>
            <ChatReplyPreview
              message={{
                id: replyingTo.id,
                userId: replyingTo.userId,
                username: replyingTo.username,
                usernameColor: replyingTo.usernameColor ?? null,
                usernameEffect: replyingTo.usernameEffect ?? null,
                content: replyingTo.content,
              }}
              className="mt-1 border-0 bg-transparent px-0 py-0"
            />
          </div>
          <button
            type="button"
            onClick={clearReplyingTo}
            className="rounded-md p-1 text-wall-muted hover:text-wall-text hover:bg-wall-border/40 transition-colors"
            aria-label="Cancel reply"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}
      <div className="flex gap-2 items-end">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={CHAT_CONFIG.MESSAGE_MAX_LENGTH}
          className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-wall-surface border border-wall-border text-wall-text text-sm placeholder:text-wall-muted focus:outline-hidden focus:ring-2 focus:ring-wall-accent/50 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={disabled || !draft.trim()}
          title={isTimedOut ? 'You are timed out' : undefined}
          className="shrink-0 px-3 py-2 rounded-lg bg-linear-to-r from-wall-accent to-wall-accent-secondary text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
        >
          Send
        </button>
      </div>
    </form>
  );
}
